import {useEffect, useRef} from 'react';
import Anchor from './Anchor';
import Button from './Button';
import PageImage from './PageImage';
import ICON_CLOSE from '../../../public/static/web-to-app/icon-close.svg';
import MaxWidth from '../../commonSub/component/MaxWidth';
import {WEB_TO_APP_EVENT_PROPS} from '../../commonSub/constant';
import {CLOSED_WEB_TO_APP_BANNER_KEY} from '../constant';
import useIntersection from '../hook/useIntersection';
import useLocalStorage from '../hook/useLocalStorage';
import useTypedSelector from '../hook/useTypedSelector';
import {parseDate} from '../util/date';
import {routeTo} from '../util/PageRouter';
import {sendEvent} from '../util/tracking';

interface IProps {
  isViewerPage?: boolean;
  className?: string;
  bannerClassName?: string;
  onSendEvent?: () => void;
  onIntersection?: (isIntersecting: boolean) => void;
  onClickClose?: () => void;
}

export default function WebToAppBanner({
  isViewerPage,
  className,
  bannerClassName,
  onSendEvent,
  onIntersection,
  onClickClose,
}: IProps) {
  const [expireTime, setExpireTime] = useLocalStorage<number>({
    key: CLOSED_WEB_TO_APP_BANNER_KEY,
    initialValue: 0,
  });
  useEffect(() => {
    expireTime > 0 && expireTime <= Date.now() && setExpireTime(0);
  }, [expireTime, setExpireTime]);

  const webToAppBanner = useTypedSelector(state =>
    isViewerPage
      ? state.common.webToAppBanner.viewer
      : state.common.webToAppBanner.normal
  );
  const isClosedBanner = !isViewerPage && expireTime > 0;
  const showBanner = !(!webToAppBanner || isClosedBanner);
  const targetRef = useRef<HTMLDivElement>(null);
  useIntersection(
    {
      targetRef,
      onIntersection,
    },
    !showBanner
  );

  if (!showBanner) {
    return null;
  }

  const bannerEl = webToAppBanner && (
    <div
      className={cn('w-full relative mx-auto', {
        'px-20': isViewerPage,
        'max-w-screen-sm': !isViewerPage,
      })}
    >
      <Anchor
        onClick={() => {
          if (onSendEvent) {
            onSendEvent();
          } else {
            sendWebToAppClickEvent();
          }
          routeTo(webToAppBanner.landingUri);
        }}
        display="flex"
        className="h-full mx-auto flex-center max-w-[275px]"
        target="_blank"
      >
        <PageImage src={webToAppBanner.image} alt="" className="w-full" />
      </Anchor>
      {!isViewerPage && (
        <Button
          className="!absolute h-24 right-20 top-20 w-24"
          onClick={() => {
            sendWebToAppEvent(true);
            onClickClose?.();
            const expireDate = parseDate();
            expireDate.setDate(expireDate.getDate() + 1);
            expireDate.setHours(0, 0, 0, 0);
            setExpireTime(expireDate.getTime());
          }}
        >
          <img src={ICON_CLOSE} width="24" height="24" alt="Close" />
        </Button>
      )}
    </div>
  );
  return (
    <>
      {!isViewerPage && (
        <div
          ref={targetRef}
          className="w-full fixed bg-dark-grey-13 bottom-0 left-0 z-toastMinus1"
        >
          <MaxWidth>{bannerEl}</MaxWidth>
        </div>
      )}
      {isViewerPage && (
        <div ref={targetRef} className={cn('px-20', className)}>
          <div className={cn('bg-dark-grey-03', bannerClassName)}>
            {bannerEl}
          </div>
        </div>
      )}
    </>
  );
}

function sendWebToAppClickEvent() {
  sendWebToAppEvent(false);
}

function sendWebToAppEvent(isClose: boolean) {
  sendEvent({
    section: '{{section}}',
    page: '{{page}}',
    action: 'click',
    actionName: isClose ? 'banner_close' : 'banner',
    customProps: WEB_TO_APP_EVENT_PROPS,
  });
}
